/**
 * Die Kandidaten für das Titelbild — welche Fotos zur Wahl stehen und in
 * welcher Reihenfolge.
 *
 * ## Warum eine zweite Datei neben `titelbild.ts`
 *
 * `titelbild.ts` beantwortet die Frage „welches Bild gilt?" — es liest die
 * Wahl aus `settings` und schreibt sie zurück. Diese Datei beantwortet die
 * Frage davor: „welche Bilder gibt es überhaupt, und wie stehen sie da?".
 * Beides in eine Datei zu legen hieße, die Vorrangregel und die Bildersammlung
 * am selben Ort zu pflegen; sie ändern sich aus verschiedenen Gründen.
 *
 * ## Die Regel, um die es hier geht
 *
 * Zur Wahl stehen **alle** Fotos der Reise, auch die aus zurückgehaltenen
 * Einträgen — der Reisende darf sich sein eigenes Bild aussuchen, und seine
 * Reiseliste sieht ohnehin nur, wer seine Reisen sehen darf. Aber ein
 * ungeteiltes Foto ist nicht dasselbe wie ein geteiltes, und der Unterschied
 * ist keine Kleinigkeit: Auf die geteilte Seite geht es **nicht** (der Server
 * fällt dort auf die Automatik zurück, siehe `oeffentlich.titelbild`). Wer das
 * nicht weiß, sucht ein Bild aus und wundert sich, warum die Familie ein
 * anderes sieht.
 *
 * Zwei Dinge folgen daraus, und beide stehen hier als reine Funktion:
 *
 * 1. **Geteilte zuerst.** Nicht als Wertung, sondern damit die Bilder, die
 *    überall gelten, ohne Blättern dastehen.
 * 2. **Die ungeteilten sind gekennzeichnet.** Ein Raster, in dem beide
 *    Sorten gleich aussehen, ist die schlechtere Fassung von gar keiner Wahl.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, keine Uhr.
 */

import { heimatstationen } from './heimatstation';

/** Der Halbsatz an einem ungeteilten Foto. Wörtlich, weil ein Test ihn hält. */
export const NICHT_GETEILT =
  'nicht geteilt — erscheint nur bei dir, nicht auf der geteilten Seite';

/** Ein Foto, wie das Raster es braucht. */
export interface Bildkandidat {
  /** Der Tagebucheintrag, in dem das Foto hängt. */
  eintrag: number;
  /** Das Medium selbst — beide Zahlen zusammen sind der Weg zum Bild. */
  medium: number;
  /** Stammt es aus einem geteilten Eintrag? Entscheidet über die Kennzeichnung. */
  geteilt: boolean;
  /**
   * Stammt es aus der Gegend, für die gerade gewählt wird?
   *
   * Immer `false`, solange es um das Titelbild der ganzen Reise geht — dort
   * gibt es keine Gegend, zu der ein Foto „gehören" könnte. Siehe
   * `kandidaten`.
   */
  imKapitel: boolean;
  /** Die Bildunterschrift oder der Titel des Eintrags — für den Alternativtext. */
  beschriftung: string | null;
  /** `2026-08-05T21:06:11` — die Wanduhrzeit des Eintrags, nur zum Sortieren. */
  ts: string | null;
  breite: number | null;
  hoehe: number | null;
}

/**
 * Die Gegend, für die gerade gewählt wird — und die ganze Reise darum herum.
 *
 * Beides wird gebraucht, und das ist kein Ballast: `gegend` beantwortet „liegt
 * diese Station in der Gegend?", `reihenfolge` beantwortet „an welcher Station
 * hing dieser Eintrag überhaupt?" für die Einträge ohne Ortsangabe. Ohne die
 * zweite Angabe fielen genau die Fotos durch, die der Befund vom 20.9.2026
 * betrifft.
 */
export interface Gegendstationen {
  /** Die Stationen **dieser** Gegend — `stops.id`. */
  gegend: ReadonlySet<number>;
  /** Alle Stationen der Reise in Reisereihenfolge — `stops.id`. */
  reihenfolge: readonly number[];
}

/** Was diese Datei von einem Tagebucheintrag braucht — mehr nicht. */
interface EintragMitMedien {
  id: number;
  ts?: string | null;
  title?: string | null;
  shared?: boolean;
  /** Die Station, an der er hängt — `null` heißt: unterwegs geschrieben. */
  stop_id?: number | null;
  medien?: {
    id: number;
    art: string;
    titel?: string | null;
    breite?: number | null;
    hoehe?: number | null;
  }[];
}

/**
 * Alle Fotos der Reise, geteilte zuerst, in jeder Gruppe die jüngsten oben.
 *
 * ## Warum nur Fotos
 *
 * Dieselbe Antwort wie überall sonst am Titelbild: Ein Video hat nicht
 * zwingend ein Standbild, das etwas zeigt, und eine Tonaufnahme gar keines.
 * Ein graues Kästchen in der Wahl wäre ein Angebot, das beim Anklicken eine
 * leere Kachel in der Reiseliste ergibt.
 *
 * ## Warum die Reihenfolge nicht der Zeitleiste folgt
 *
 * Das Tagebuch steht neueste zuerst, und diese Sortierung übernimmt die
 * Kandidatenliste **innerhalb** jeder Gruppe: Wer ein Bild sucht, denkt an
 * „das vom Ledrosee", und das war vorgestern. Die Trennung geteilt/ungeteilt
 * liegt darüber, weil sie eine andere Frage beantwortet — nicht „wann", sondern
 * „wo gilt das".
 *
 * Sortiert wird über die Zeichenkette und nicht über `new Date`: Der
 * Zeitstempel kommt ohne Zone, und ein Datumsparser läse ihn als Ortszeit des
 * Browsers (siehe der Kopf von `tagebuch.ts`). Fürs Sortieren spielt das keine
 * Rolle, solange **alle** gleich behandelt werden — und genau das tut ein
 * Vergleich zweier ISO-Zeichenketten. Bei gleicher Sekunde entscheidet die
 * Medienkennung, damit die Liste bei jedem Öffnen dieselbe ist.
 *
 * ## Die Gegend steht über allem
 *
 * Seit dem 20.9.2026 wählt derselbe Dialog auch das Bild **eines Kapitels**.
 * Dann kommen die Fotos dieser Gegend nach oben, und zwar über die Trennung
 * geteilt/ungeteilt hinweg: Wer ein Bild für die Toskana sucht, sucht es unter
 * den Toskana-Fotos, und ein geteiltes Bild vom Gardasee ist dafür kein
 * besserer Vorschlag als ein zurückgehaltenes aus Lucca. Innerhalb der beiden
 * Blöcke bleibt alles, wie es war — erst geteilt, dann die Zeit.
 *
 * Zur Wahl stehen trotzdem **alle** Fotos der Reise. Ein Raster, das nur die
 * Gegend zeigte, nähme dem Reisenden die Entscheidung ab, ein Bild von der
 * Anfahrt über das Kapitel zu setzen — und das ist seine, nicht unsere.
 *
 * `gegend` nennt die **Stationskennungen** dieser Gegend, nicht die der
 * Einträge. Zwei Gründe: Welche Station in welcher Gegend liegt, rechnet
 * `kapitel.kapitelVon` aus den Stationen, die der Aufrufer ohnehin hält — die
 * Einträge dagegen holt dieser Dialog selbst, und eine Liste ihrer Kennungen
 * müsste erst dafür geladen werden.
 *
 * Ein Eintrag **ohne** Station zählt zur Gegend seiner Heimatstation
 * (`heimatstation.heimatstationen`, dieselbe Regel wie in der Zeitleiste und
 * in der Bildautomatik). Deshalb kommt die ganze Reisereihenfolge mit und
 * nicht nur die Stationen der Gegend: Die Heimat eines Zwischenhalts kann
 * überall in der Reise liegen, und ob sie in dieser Gegend liegt, ist genau
 * die Frage. Befund vom 20.9.2026 — ohne das standen die vier Turiner Fotos
 * aus dem Parkhaus ganz unten bei den Fotos fremder Gegenden.
 */
export function kandidaten(
  eintraege: EintragMitMedien[],
  gegend?: Gegendstationen | null
): Bildkandidat[] {
  const heimat = gegend
    ? heimatstationen(gegend.reihenfolge, eintraege, (e) => e.stop_id ?? null)
    : null;
  const alle: Bildkandidat[] = [];
  for (const e of eintraege) {
    const zuhause = heimat?.get(e.id) ?? null;
    const drin = zuhause != null && gegend?.gegend.has(zuhause) === true;
    for (const m of e.medien ?? []) {
      if (m.art !== 'foto') continue;
      alle.push({
        eintrag: e.id,
        medium: m.id,
        geteilt: e.shared === true,
        imKapitel: drin,
        // Die Bildunterschrift zuerst, dann der Titel des Eintrags: Beides
        // dient nur dem Alternativtext, und „Am Ledrosee" sagt mehr als
        // „Foto 3". Leerer Text zählt als keiner.
        beschriftung: (m.titel || e.title || '').trim() || null,
        ts: e.ts ?? null,
        breite: m.breite ?? null,
        hoehe: m.hoehe ?? null
      });
    }
  }
  return alle.sort((a, b) => {
    if (a.imKapitel !== b.imKapitel) return a.imKapitel ? -1 : 1;
    if (a.geteilt !== b.geteilt) return a.geteilt ? -1 : 1;
    const zeit = (b.ts ?? '').localeCompare(a.ts ?? '');
    return zeit !== 0 ? zeit : b.medium - a.medium;
  });
}

/**
 * Gibt es in dieser Reise überhaupt etwas zu wählen?
 *
 * Der Dialog braucht die Antwort, um zwischen dem Raster und einem Satz zu
 * unterscheiden — ein leeres Raster mit einem Abbrechen-Knopf wäre eine Fläche,
 * die nicht sagt, warum sie leer ist.
 */
export function hatFotos(eintraege: EintragMitMedien[]): boolean {
  return eintraege.some((e) => (e.medien ?? []).some((m) => m.art === 'foto'));
}

/**
 * Wie viele der Kandidaten kommen aus zurückgehaltenen Einträgen?
 *
 * Der Dialog erklärt den Unterschied **nur dann**, wenn es ihn gibt. Ein Satz
 * über Fotos, die nicht auf die geteilte Seite gehen, ist bei einer Reise, in
 * der alles geteilt ist, eine Warnung vor nichts — und die nächste Warnung
 * liest dann auch niemand mehr.
 */
export function ungeteilte(liste: Bildkandidat[]): number {
  return liste.filter((k) => !k.geteilt).length;
}
